import type { ProductVariant } from "@shopify/hydrogen-react/storefront-api-types";
import type { FragmentOfShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { toLower, upperFirst } from "lodash/fp";
import type {
  ImageObject as ImageObjectSchema,
  Offer as OfferSchema,
  PaymentMethod as PaymentMethodSchema,
  SizeSpecification,
  WithContext,
} from "schema-dts";
import { readFragmentShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { Image_Fragment } from "#/graphql/shopify-storefront/fragments";
import {
  LinkedData_Localization_Fragment,
  LinkedData_PaymentSettings_Fragment,
  LinkedData_Product_Fragment,
  LinkedData_ProductVariant_Fragment,
  LinkedData_Shop_Fragment,
} from "#/components/linked-data/graphql";

export function transformShopifyShopToPaymentMethodSchema(
  paymentSettingsMasked: FragmentOfShopifyStorefront<
    typeof LinkedData_PaymentSettings_Fragment
  >,
): PaymentMethodSchema[] {
  const paymentSettings = readFragmentShopifyStorefront(
    LinkedData_PaymentSettings_Fragment,
    paymentSettingsMasked,
  );

  const acceptedPaymentMethod =
    paymentSettings.acceptedCardBrands.map(
      (acceptedCardBrand) =>
        `http://purl.org/goodrelations/v1#${acceptedCardBrand
          .split("_")
          .map((word) => upperFirst(toLower(word)))
          .join("")}` as unknown as PaymentMethodSchema,
    ) ?? [];

  return acceptedPaymentMethod;
}

export function transformShopifyProductToAggregateOfferSchema({
  localizationMasked,
  productMasked,
  shopMasked,
}: {
  localizationMasked: FragmentOfShopifyStorefront<
    typeof LinkedData_Localization_Fragment
  >;
  productMasked: FragmentOfShopifyStorefront<
    typeof LinkedData_Product_Fragment
  >;
  shopMasked: FragmentOfShopifyStorefront<typeof LinkedData_Shop_Fragment>;
}): WithContext<OfferSchema> {
  const shop = readFragmentShopifyStorefront(
    LinkedData_Shop_Fragment,
    shopMasked,
  );

  const acceptedPaymentMethod = transformShopifyShopToPaymentMethodSchema(
    shop.paymentSettings,
  );

  const localization = readFragmentShopifyStorefront(
    LinkedData_Localization_Fragment,
    localizationMasked,
  );

  const product = readFragmentShopifyStorefront(
    LinkedData_Product_Fragment,
    productMasked,
  );

  const releaseDate = product.releaseDate?.value.split("T")[0];

  return {
    "@context": "https://schema.org",
    "@type": "AggregateOffer",
    acceptedPaymentMethod: [
      {
        "@type": "LoanOrCredit",
        amount: {
          "@type": "MonetaryAmount",
          currency: localization.country.currency.isoCode,
          value: product.priceRange.minVariantPrice.amount,
        },
        currency: localization.country.currency.isoCode,
        loanTerm: {
          "@type": "QuantitativeValue",
          maxValue: 4,
          minValue: 1,
          unitCode: "MON",
          unitText: "month",
        },
      },
      ...acceptedPaymentMethod,
    ],
    areaServed: localization.country.name,
    availability: product.availableForSale ? "InStock" : "OutOfStock",
    ...(releaseDate && {
      availabilityStarts: new Date(releaseDate).toISOString(),
    }),
    category: product.productType,
    highPrice: product.priceRange.maxVariantPrice.amount,
    lowPrice: product.priceRange.minVariantPrice.amount,
    priceCurrency: product.priceRange.minVariantPrice.currencyCode,
    url: `/products/${product.handle}`,
  };
}

export function transformShopifyOfferToOfferSchema({
  localizationMasked,
  productMasked,
  shopMasked,
  variantMasked,
}: {
  localizationMasked: FragmentOfShopifyStorefront<
    typeof LinkedData_Localization_Fragment
  >;
  productMasked: FragmentOfShopifyStorefront<
    typeof LinkedData_Product_Fragment
  >;
  shopMasked: FragmentOfShopifyStorefront<typeof LinkedData_Shop_Fragment>;
  variantMasked: FragmentOfShopifyStorefront<
    typeof LinkedData_ProductVariant_Fragment
  >;
}): WithContext<OfferSchema> {
  const shop = readFragmentShopifyStorefront(
    LinkedData_Shop_Fragment,
    shopMasked,
  );

  const acceptedPaymentMethod = transformShopifyShopToPaymentMethodSchema(
    shop.paymentSettings,
  );

  const localization = readFragmentShopifyStorefront(
    LinkedData_Localization_Fragment,
    localizationMasked,
  );

  const product = readFragmentShopifyStorefront(
    LinkedData_Product_Fragment,
    productMasked,
  );

  const variant = readFragmentShopifyStorefront(
    LinkedData_ProductVariant_Fragment,
    variantMasked,
  );

  const releaseDate = product.releaseDate?.value.split("T")[0];

  return {
    "@context": "https://schema.org",
    "@type": "Offer",
    acceptedPaymentMethod: [
      {
        "@type": "LoanOrCredit",
        amount: {
          "@type": "MonetaryAmount",
          currency: localization.country.currency.isoCode,
          value: product.priceRange.minVariantPrice.amount,
        },
        currency: localization.country.currency.isoCode,
        loanTerm: {
          "@type": "QuantitativeValue",
          maxValue: 4,
          minValue: 1,
          unitCode: "MON",
          unitText: "month",
        },
      },
      ...acceptedPaymentMethod,
    ],
    areaServed: localization.country.name,
    availability: product.availableForSale ? "InStock" : "OutOfStock",
    ...(releaseDate && {
      availabilityStarts: new Date(releaseDate).toISOString(),
    }),
    category: product.productType,
    price: product.priceRange.minVariantPrice.amount,
    priceCurrency: product.priceRange.minVariantPrice.currencyCode,
    url: `/products/${product.handle}?${variant.selectedOptions.map((selectedOption) => `${selectedOption.name.toLowerCase()}=${selectedOption.value.toLowerCase()}`).join("&")}`,
  };
}

export function transformShopifyProductVariantMaskedToSizeSpecificationSchema({
  variantMasked,
}: {
  variantMasked: FragmentOfShopifyStorefront<
    typeof LinkedData_ProductVariant_Fragment
  >;
}): WithContext<SizeSpecification> {
  const variant = readFragmentShopifyStorefront(
    LinkedData_ProductVariant_Fragment,
    variantMasked,
  );

  return {
    "@context": "https://schema.org",
    "@type": "SizeSpecification",
    sizeSystem: "https://schema.org/WearableSizeSystemEurope",
    sizeGroup: [
      "https://schema.org/WearableSizeGroupWomens",
      "https://schema.org/WearableSizeGroupRegular",
    ],
    name: variant.selectedOptions.find(
      (selectedOption) => selectedOption.name === "Size",
    )?.value,
    // hasMeasurement: [
    //   {
    //     "@type": "QuantitativeValue",
    //     valueReference: "https://schema.org/WearableMeasurementChestOrBust",
    //     unitCode: "CMT",
    //     value: 52,
    //   },
    // ],
    suggestedGender: "female",
    suggestedAge: {
      "@type": "QuantitativeValue",
      name: "adult",
      unitCode: "ANN",
      minValue: 16,
    },
    // suggestedMeasurement: [
    //   {
    //     "@type": "QuantitativeValue",
    //     valueReference: "https://schema.org/BodyMeasurementChest",
    //     unitCode: "CMT",
    //     minValue: 51,
    //     maxValue: 54,
    //   },
    // ],
  };
}

export function transformShopifyProductVariantToSizeSpecificationSchema({
  variant,
}: {
  variant: ProductVariant;
}): WithContext<SizeSpecification> {
  return {
    "@context": "https://schema.org",
    "@type": "SizeSpecification",
    sizeSystem: "https://schema.org/WearableSizeSystemEurope",
    sizeGroup: [
      "https://schema.org/WearableSizeGroupWomens",
      "https://schema.org/WearableSizeGroupRegular",
    ],
    name: variant.selectedOptions.find(
      (selectedOption) => selectedOption.name === "Size",
    )?.value,
    // hasMeasurement: [
    //   {
    //     "@type": "QuantitativeValue",
    //     valueReference: "https://schema.org/WearableMeasurementChestOrBust",
    //     unitCode: "CMT",
    //     value: 52,
    //   },
    // ],
    suggestedGender: "female",
    suggestedAge: {
      "@type": "QuantitativeValue",
      name: "adult",
      unitCode: "ANN",
      minValue: 16,
    },
    // suggestedMeasurement: [
    //   {
    //     "@type": "QuantitativeValue",
    //     valueReference: "https://schema.org/BodyMeasurementChest",
    //     unitCode: "CMT",
    //     minValue: 51,
    //     maxValue: 54,
    //   },
    // ],
  };
}

export function transformShopifyImageToImageObjectSchema({
  about,
  imageMasked,
}: {
  about: string;
  imageMasked: FragmentOfShopifyStorefront<typeof Image_Fragment>;
}): ImageObjectSchema {
  const image = readFragmentShopifyStorefront(Image_Fragment, imageMasked);

  return {
    "@type": "ImageObject",
    about: image?.altText || about,
    height: image?.height?.toString(),
    representativeOfPage: true,
    url: image?.url,
    width: image?.width?.toString(),
  };
}
